
import { useState, useEffect } from 'react';

interface FacebookIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FacebookIntegration({ isOpen, onClose }: FacebookIntegrationProps) {
  const [activeTab, setActiveTab] = useState('page');
  const [shareUrl, setShareUrl] = useState(window.location.href);
  const [shareText, setShareText] = useState('Check out MAVESKY TRAVELS - Your trusted partner for global flight bookings!');

  const handleFacebookShare = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, 'facebook-share', 'width=600,height=400,scrollbars=yes,resizable=yes');
  };

  const handleMessengerShare = () => {
    const url = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=YOUR_APP_ID&redirect_uri=${encodeURIComponent(window.location.href)}`;
    window.open(url, 'messenger-share', 'width=600,height=400,scrollbars=yes,resizable=yes');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Facebook Integration</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => setActiveTab('page')}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'page' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Facebook Page
            </button>
            <button
              onClick={() => setActiveTab('share')}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'share' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Share Tools
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'reviews' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Social Proof
            </button>
          </div>

          {/* Facebook Page Tab */}
          {activeTab === 'page' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className="ri-facebook-fill text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">MAVESKY TRAVELS</h3>
                    <p className="text-gray-600">Travel & Tourism</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-sm text-gray-500">
                        <i className="ri-user-line mr-1"></i>
                        12,847 followers
                      </span>
                      <span className="text-sm text-gray-500">
                        <i className="ri-star-fill text-yellow-500 mr-1"></i>
                        4.8 rating
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-thumb-up-line mr-2"></i>
                    Like Page
                  </button>
                  <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-message-line mr-2"></i>
                    Message
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800">Recent Posts</h4>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <i className="ri-facebook-fill text-white"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">MAVESKY TRAVELS</p>
                      <p className="text-sm text-gray-500">2 hours ago</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-3">
                    🌍 Special offer alert! Book your dream destination with up to 40% off on international flights. 
                    Limited time offer - don't miss out! ✈️
                  </p>
                  <div className="bg-gray-100 rounded-lg p-3 mb-3">
                    <img 
                      src="https://public.readdy.ai/ai/img_res/6037f8b912e745a87c058de8ed6a1dcb.jpg"
                      alt="Travel destination"
                      className="w-full h-32 object-cover rounded-lg mb-2"
                    />
                    <p className="text-sm font-medium text-gray-800">Exclusive Flight Deals</p>
                    <p className="text-xs text-gray-600">maveskytravels.com</p>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-4">
                      <span><i className="ri-thumb-up-line mr-1"></i>247 likes</span>
                      <span><i className="ri-chat-3-line mr-1"></i>18 comments</span>
                      <span><i className="ri-share-line mr-1"></i>32 shares</span>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <i className="ri-facebook-fill text-white"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">MAVESKY TRAVELS</p>
                      <p className="text-sm text-gray-500">1 day ago</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-3">
                    Thank you to all our amazing customers! 🙏 Your trust and support help us provide the best travel experiences. 
                    Here's what Sarah M. said about her recent booking...
                  </p>
                  <div className="bg-blue-50 border-l-4 border-blue-600 p-3 mb-3">
                    <p className="text-gray-700 italic">
                      "Incredible service! MAVESKY TRAVELS made my family vacation planning so easy. 
                      Great prices and excellent customer support. Highly recommended!"
                    </p>
                    <p className="text-sm text-gray-600 mt-2">- Sarah M., verified customer</p>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-4">
                      <span><i className="ri-thumb-up-line mr-1"></i>189 likes</span>
                      <span><i className="ri-chat-3-line mr-1"></i>24 comments</span>
                      <span><i className="ri-share-line mr-1"></i>15 shares</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Share Tools Tab */}
          {activeTab === 'share' && (
            <div className="space-y-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Share URL:
                </label>
                <input
                  type="url"
                  value={shareUrl}
                  onChange={(e) => setShareUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:outline-none focus:border-blue-500"
                  placeholder="https://maveskytravels.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Share Message:
                </label>
                <textarea
                  value={shareText}
                  onChange={(e) => setShareText(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:outline-none focus:border-blue-500 resize-none"
                  placeholder="Add your message..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleFacebookShare}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-facebook-fill"></i>
                  Share on Facebook
                </button>
                <button
                  onClick={handleMessengerShare}
                  className="bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-messenger-line"></i>
                  Send via Messenger
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3">Share Preview</h4>
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-white"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Your Name</p>
                      <p className="text-sm text-gray-500">Just now</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-3">{shareText}</p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=modern%20travel%20website%20homepage%20with%20airplane%20and%20world%20map%2C%20professional%20business%20design%2C%20clean%20layout%2C%20travel%20booking%20interface&width=400&height=200&seq=fb-share-preview&orientation=landscape"
                      alt="Website preview"
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-3">
                      <p className="font-medium text-gray-800">MAVESKY TRAVELS</p>
                      <p className="text-sm text-gray-600">Your trusted partner for global flight bookings</p>
                      <p className="text-xs text-gray-500 uppercase">{shareUrl}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Social Proof Tab */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Customer Reviews from Facebook</h3>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="flex text-yellow-500">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="text-gray-600">4.8/5 based on 342 reviews</span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <img 
                      src="https://readdy.ai/api/search-image?query=professional%20headshot%20of%20happy%20business%20woman%20smiling%2C%20corporate%20portrait%2C%20clean%20background&width=40&height=40&seq=review-1&orientation=squarish"
                      alt="Customer"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-gray-800">Emily Rodriguez</p>
                      <div className="flex items-center gap-2">
                        <div className="flex text-yellow-500 text-sm">
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                        </div>
                        <span className="text-sm text-gray-500">2 days ago</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700">
                    "Outstanding service! Booked my honeymoon flights through MAVESKY and everything was perfect. 
                    Great prices, easy booking process, and excellent customer support. Will definitely use again!"
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <button className="hover:text-blue-600 cursor-pointer">
                      <i className="ri-thumb-up-line mr-1"></i>Helpful (23)
                    </button>
                    <button className="hover:text-blue-600 cursor-pointer">
                      <i className="ri-chat-3-line mr-1"></i>Reply
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <img 
                      src="https://readdy.ai/api/search-image?query=professional%20headshot%20of%20confident%20businessman%20smiling%2C%20corporate%20portrait%2C%20clean%20background&width=40&height=40&seq=review-2&orientation=squarish"
                      alt="Customer"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-gray-800">Michael Chen</p>
                      <div className="flex items-center gap-2">
                        <div className="flex text-yellow-500 text-sm">
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                        </div>
                        <span className="text-sm text-gray-500">1 week ago</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700">
                    "Been using MAVESKY for all my business travels. Their corporate booking system is fantastic, 
                    and I always get competitive rates. The mobile app makes managing bookings so convenient."
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <button className="hover:text-blue-600 cursor-pointer">
                      <i className="ri-thumb-up-line mr-1"></i>Helpful (18)
                    </button>
                    <button className="hover:text-blue-600 cursor-pointer">
                      <i className="ri-chat-3-line mr-1"></i>Reply
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <img 
                      src="https://readdy.ai/api/search-image?query=professional%20headshot%20of%20friendly%20woman%20smiling%2C%20corporate%20portrait%2C%20clean%20background&width=40&height=40&seq=review-3&orientation=squarish"
                      alt="Customer"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-gray-800">Sarah Thompson</p>
                      <div className="flex items-center gap-2">
                        <div className="flex text-yellow-500 text-sm">
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-fill"></i>
                          <i className="ri-star-line"></i>
                        </div>
                        <span className="text-sm text-gray-500">2 weeks ago</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700">
                    "Great experience overall! Found amazing deals for our family vacation to Europe. 
                    The only minor issue was a slight delay in email confirmation, but customer service resolved it quickly."
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <button className="hover:text-blue-600 cursor-pointer">
                      <i className="ri-thumb-up-line mr-1"></i>Helpful (12)
                    </button>
                    <button className="hover:text-blue-600 cursor-pointer">
                      <i className="ri-chat-3-line mr-1"></i>Reply
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-facebook-fill text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Connect with us on Facebook</p>
                    <p className="text-sm text-gray-600">Join our community for travel tips, deals, and updates</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
